import { some } from 'lodash-es';
import { useState, Fragment } from 'react';
import { useFormikContext } from 'formik';
import Snackbar from '@mui/material/Snackbar';
import SnackbarContent from '@mui/material/SnackbarContent';
import { green } from '@mui/material/colors';
import { makeStyles } from 'tss-react/mui';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import flattenObject from '../../utils/flatten-object';

const useStyles = makeStyles()((theme) => ({
  success: {
    backgroundColor: green[600],
  },
  error: {
    backgroundColor: theme.palette.error.dark,
  },
  close: {
    padding: theme.spacing(0.5)
  },
  icon: {
    fontSize: 20,
  },
  iconVariant: {
    opacity: 0.9,
    marginRight: theme.spacing(1),
  },
  message: {
    display: 'flex',
    alignItems: 'center',
  },
}));

type GraphQLError = {
  message: string;
  [key: string]: unknown;
};

type ErrorInfo = {
  errors: Record<string, unknown>;
  status?: {
    networkError?: unknown;
    graphQLErrors?: GraphQLError[];
  };
};

const getErrorType = ({ errors, status }: ErrorInfo) => {
  if (status) {
    if (status.networkError) {
      return 'networkError';
    }
    if (status.graphQLErrors && status.graphQLErrors.length) {
      return 'graphQLErrors';
    }
  }
  if (some(flattenObject(errors), (v) => v)) {
    return 'formikErrors';
  }
  return null;
};

type Props = {
  successMessage?: React.ReactNode;
  autoHideDuration?: number;
};

const FormSubmitStatus = ({ successMessage = 'Saved!', autoHideDuration = 3000 }: Props) => {
  const { isSubmitting, errors, status: formikStatus } = useFormikContext();
  const { classes, cx } = useStyles();
  const [status, setStatus] = useState<string | null>(null);
  const [wasSubmitting, setWasSubmitting] = useState(false);

  if (isSubmitting && !wasSubmitting) {
    setWasSubmitting(true);
  }

  if (!isSubmitting && wasSubmitting) {
    setWasSubmitting(false);
    setStatus(getErrorType({ errors, status: formikStatus }) || 'success');
  }

  function handleClose() {
    setStatus(null);
  }

  return (
    <Fragment>
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        open={status === "networkError" || status === 'graphQLErrors' || status === 'formikErrors'}
        autoHideDuration={autoHideDuration}
        onClose={handleClose}
      >
        <SnackbarContent
          className={classes.error}
          aria-describedby="snackbar-message"
          message={
            <span id="snackbar-message" className={classes.message}>
              <ErrorIcon className={cx(classes.icon, classes.iconVariant)} />
              {status === 'formikErrors' && (
                'There were errors with your submission'
              )}
              {status === 'graphQLErrors' && (
                'There were errors with your submission'
              )}
              {status === 'networkError' && (
                'A network error occurred'
              )}
            </span>
          }
          action={[
            <IconButton key="close" aria-label="close" color="inherit" onClick={handleClose}>
              <CloseIcon className={classes.icon} />
            </IconButton>
          ]}
        />
      </Snackbar>
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        open={status === 'success'}
        autoHideDuration={3000}
        onClose={handleClose}
      >
        <SnackbarContent
          className={classes.success}
          aria-describedby="snackbar-message"
          message={
            <span id="snackbar-message" className={classes.message}>
              <CheckCircleIcon className={cx(classes.icon, classes.iconVariant)} />
              {successMessage}
            </span>
          }
          action={[
            <IconButton key="close" aria-label="close" color="inherit" onClick={handleClose}>
              <CloseIcon className={classes.icon} />
            </IconButton>
          ]}
        />
      </Snackbar>
    </Fragment>
  );
};

export default FormSubmitStatus;
